// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Box, useTheme } from '@mui/system';
import { Typography, Checkbox, FormControlLabel, ListItem, ListItemButton, IconButton, Collapse, Divider, SvgIcon, Select, MenuItem, SelectChangeEvent, ClickAwayListener, Popover, TextField } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { useSelector } from 'react-redux';
import store, { RootState } from 'Store';
import Sidebar from 'Components/Sidebar';
import { setLoadedGdbId } from 'Features/dashboard/dashboardSlice';
import { getOrders } from 'Features/order/orderSelectors';
import { SetOrderSelectedAction } from 'Features/map/mapEditorActions';
import { orderStatusCode } from 'Utils/constants';
import { getLegendClasses } from 'Utils/pointCloudClasses';
import SceneView from '@arcgis/core/views/SceneView';
import PointCloudLayer from '@arcgis/core/layers/PointCloudLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import esriRequest from '@arcgis/core/request';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import { GetEsriGeoJsonUrl } from 'Config';
import getRendererForConfig from 'Utils/esriRenderer';
import CadDrawings from './CadDrawings';
import ElevationOffsetInfo, { ElevationOffsetIcon, GeoidStatus } from './ElevationOffsetInfo';
import { EnsureGeoidOffsetReason } from '../elevationThunk';
import { resolveOrderCadUnits, resolveFeetPerMetre, DisplayUnit } from '../cadUnits';
import { formatOffsetDraft, parseOffsetDraftToMetres, PendingGeoidState } from '../elevationOffset';
import Orthomosaic from './Orthomosaic';
import stylesDef from './styles';
import config from '../mainconfig';
import { styleConfig } from '../styleConfig';
import { findRule } from '../rendererRules';

interface ILasFile {
  _id: string;
  name: string;
  ownerOrder?: string;
  lasFilename: string;
  filename: string;
  lasType?: 'unclassified' | 'all_classes';
  newClasses: boolean;
  esriUrl?: string;
  esriError?: string;
}
interface LayerInfo {
  id: string;
  name: string;
  url: string;
  color?: string;
  geometryType?: string;
}

interface IGeoDbFile {
  _id: string;
  filename: string;
  esriUrl?: Record<string, string>[];
  esriError?: string;
  createdAt?: string;
}

interface IShpFile {
  _id: string;
  filename: string;
  esriUrl?: Record<string, string>[];
  esriError?: string;
  createdAt?: string;
}

interface FilesSidebarProps {
  lasFilesWith3DTile: ILasFile[];
  showPointCloud: boolean;
  setShowPointCloud: (show: boolean) => void;
  setResetZoom: (show: boolean) => void;
  toggleLasVisibility: (filename: string, visible: boolean) => void;
  lasFilesDisplayStatuses: { [key: string]: boolean };
  showClassifiedPointCloud: { [key: string]: boolean };
  setShowClassifiedPointCloud: (show: { [key: string]: boolean }) => void;
  onToggleSidebar: (visible: boolean) => void;
  isSidebarVisible: boolean;
  toggleUnknownClassVisibility: (fileTitle: string, visible: boolean) => void;
  toggleClassVisibility: (fileTitle: string, classId: string, visible: boolean) => void;
  setAllClassesVisibility: (fileTitle: string, visible: boolean) => void;
  classVisibility: { [fileTitle: string]: { [classId: string]: boolean } };
  classesPresentByFile: { [fileTitle: string]: number[] | null };
  openAdjustPanel?: () => void;
  onAdjustPanelPositionChange?: (position: { top: number; left: number } | null) => void;
  view?: __esri.SceneView;
  geodatabaseLayersRef?: React.MutableRefObject<any[]>;
  shapefileLayersRef?: React.MutableRefObject<any[]>;
  // "KML Boundary" section (AUT-6431): the parent owns the boundary layer, so the sidebar
  // only renders the checkbox state and reports toggles back up.
  kmlBoundaryVisible: boolean;
  kmlBoundaryAvailable: boolean;
  onKmlBoundaryToggle: () => void;
  userElevationOffset: number;
  onCommitUserElevationOffset: (value: number) => void;
  projectGeoidOffset: number;
  // What the draft order can say about its automatic offset while nothing is placed yet; display only.
  pendingGeoidState: PendingGeoidState;
  geoidStatus: GeoidStatus;
  geoidUnavailableReason: EnsureGeoidOffsetReason | null;
  elevationSaveFailed: boolean;
}

export const FilesSidebar = ({
  lasFilesWith3DTile,
  showPointCloud,
  lasFilesDisplayStatuses,
  showClassifiedPointCloud,
  setShowClassifiedPointCloud,
  setShowPointCloud,
  setResetZoom,
  toggleLasVisibility,
  onToggleSidebar,
  isSidebarVisible,
  toggleUnknownClassVisibility,
  toggleClassVisibility,
  setAllClassesVisibility,
  classVisibility,
  classesPresentByFile,
  openAdjustPanel,
  onAdjustPanelPositionChange,
  view,
  geodatabaseLayersRef,
  shapefileLayersRef,
  kmlBoundaryVisible,
  kmlBoundaryAvailable,
  onKmlBoundaryToggle,
  userElevationOffset,
  onCommitUserElevationOffset,
  projectGeoidOffset,
  pendingGeoidState,
  geoidStatus,
  geoidUnavailableReason,
  elevationSaveFailed,
}: FilesSidebarProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const placedOrders = useAppSelector((state: RootState) => getOrders(state).placedOrders);
  const currentPanel = useAppSelector((state: RootState) => state.map.editor.present.currentPanel);
  const orderSelected = useAppSelector((state: RootState) => state.map.editor.present.orderSelected);
  const { order3DCadFilesList } = useAppSelector((state: RootState) => state.viewer3D);
  const panoFeatures = useAppSelector((state: RootState) => state.esri.features);
  const [cadViewref, setCadView] = useState(null);
  const projectData = useSelector((state: RootState) => state.project.project);
  const lasMapInstanceRef = useRef<__esri.Map | null>(null);
  const [openErrorTooltip, setOpenErrorTooltip] = useState<string | null>(null);
  const orders = Object.keys(useAppSelector((state) => state.viewer3D?.order3DCadFilesList || {}));
  const sortedPlacedOrders = useMemo(() => [...placedOrders].sort((a, b) => {
    const dateA = new Date(a.createdAt || 0).getTime();
    const dateB = new Date(b.createdAt || 0).getTime();
    return dateB - dateA; // Oldest first (reverse chronological)
  }), [placedOrders]);
  const inputLas = lasFilesWith3DTile || [];
  const selectedOrder = placedOrders.find((o) => o._id === orderSelected);
  const selectedOrderId = selectedOrder?._id ?? null;
  const pointCloudFilesForOrder = projectData.lasFiles?.filter((f: any) =>
    f.ownerOrder === selectedOrderId && f.esriUrl) ?? [];

  const [activeCadId, setActiveCadId] = useState<string | null>(null);
  const [classifiedPointCloudExpanded, setClassifiedPointCloudExpanded] = useState(true);
  const [unclassifiedPointCloudExpanded, setUnClassifiedPointCloudExpanded] = useState(true);
  const [showAllClassesByFile, setShowAllClassesByFile] = useState<Record<string, boolean>>({});
  const [pointCloudLayers, setPointCloudLayers] = useState<PointCloudLayer[]>([]);
  const [lastClassVisibilityState, setLastClassVisibilityState] = useState<{
    [fileTitle: string]: { [classId: string]: boolean };
  }>({});
  const [adjustButtonPosition, setAdjustButtonPosition] = useState<{ top: number; left: number } | null>(null);
  const adjustButtonRef = useRef<HTMLButtonElement | null>(null);
  // Geodatabase state
  const [geodatabaseExpanded, setGeodatabaseExpanded] = useState(true);
  const [geodatabaseChecked, setGeodatabaseChecked] = useState(true);
  const [geodatabaseLayers, setGeodatabaseLayers] = useState<Record<string, boolean>>({});
  const [geodatabaseShowAll, setGeodatabaseShowAll] = useState(false);
  const [selectedGeoDbFile, setSelectedGeoDbFile] = useState<string | null>(null);
  const [geodatabaseLayersList, setGeodatabaseLayersList] = useState<any[]>([]);
  const internalGeodatabaseLayerRef = useRef<FeatureLayer[]>([]);
  const gdbRef = geodatabaseLayersRef || internalGeodatabaseLayerRef;

  // Shapefile state
  const [shapefileExpanded, setShapefileExpanded] = useState(true);
  const [shapefileChecked, setShapefileChecked] = useState(false);
  const [shapefileLayers, setShapefileLayers] = useState<Record<string, boolean>>({});
  const [shapefileShowAll, setShapefileShowAll] = useState(false);
  const [selectedShpFile, setSelectedShpFile] = useState<string | null>(null);
  const [shapefileLayersList, setShapefileLayersList] = useState<any[]>([]);
  const internalShapefileLayerRef = useRef<FeatureLayer[]>([]);
  const shpRef = shapefileLayersRef || internalShapefileLayerRef;

  const [noOrder2DExpanded, setNoOrder2DExpanded] = useState(true);
  const [noOrder3DExpanded, setNoOrder3DExpanded] = useState(true);

  // Get GeoDb and Shp files from selected order
  const geoDbFiles: IGeoDbFile[] = selectedOrder?.geoDbFiles ?? [];
  const shpFiles: IShpFile[] = selectedOrder?.shpFiles ?? [];

  // popover
  const [errorTooltipAnchor, setErrorTooltipAnchor] = useState<HTMLElement | null>(null);
  const [errorTooltipFileId, setErrorTooltipFileId] = useState<string | null>(null);

  const UNKNOWN_CLASS = {
    classId: -1, // Use -1 or any identifier that won't conflict with real class IDs
    name: 'Unknown',
    color: '#9e9e9e', // Neutral gray — never white in the classified legend
  };

  // Sync the map instance ref with the view prop
  useEffect(() => {
    if (view?.map && lasMapInstanceRef.current !== view.map) {
      lasMapInstanceRef.current = view.map;
    }
  }, [view?.map]);

  useEffect(() => {
    if (adjustButtonPosition && onAdjustPanelPositionChange) {
      onAdjustPanelPositionChange(adjustButtonPosition);
    }
  }, [adjustButtonPosition, onAdjustPanelPositionChange]);

  const handleAdjustPanelClick = (panelType: 'classified' | 'output' | 'input', event: React.MouseEvent<HTMLButtonElement>) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();

    setAdjustButtonPosition({
      top: rect.bottom,
      left: rect.left,
    });

    openAdjustPanel?.(panelType);
  };

  // Initialize Geodatabase with first file
  useEffect(() => {
    if (geoDbFiles.length > 0 && !selectedGeoDbFile) {
      const sortedFiles = [...geoDbFiles].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      });

      // Find the first file with valid esriUrl
      const validFile = sortedFiles.find(
        (file) => file.esriUrl && file.esriError?.toLowerCase() === 'success',
      );

      if (validFile) {
        setSelectedGeoDbFile(validFile._id);
        const existingGroup = lasMapInstanceRef.current?.findLayerById(validFile._id) as __esri.GroupLayer;
        if (existingGroup) {
        // Hydrate sidebar from already-loaded map layer — don't add a duplicate
          const layersList = (existingGroup.layers as any).toArray().map((layer: any) => ({
            id: (layer as any).layerId?.toString() || '0',
            name: (layer as any).layerName || layer.title,
            url: layer.url,
            geometryType: (layer as any).geometryType,
            color: (layer as any).layerColor || '#0078D4',
            layer,
          }));
          setGeodatabaseLayersList(layersList);
          const initialLayers: Record<string, boolean> = {};
          layersList.forEach((l: any) => { initialLayers[l.id] = true; });
          setGeodatabaseLayers(initialLayers);
          setGeodatabaseChecked(true);
          if (!gdbRef.current.some((l) => l.id === existingGroup.id)) {
            gdbRef.current = [...gdbRef.current, existingGroup];
          }
        } else {
          loadGeodatabaseLayers(validFile.esriUrl, validFile._id);
        }
      } else {
        // No valid files, select the most recent one anyway (will show error)
        setSelectedGeoDbFile(sortedFiles[0]._id);
      }
    }
  }, [geoDbFiles, selectedGeoDbFile]);

  // Initialize Shapefile with first file
  useEffect(() => {
    if (shpFiles.length > 0 && !selectedShpFile) {
      const sortedFiles = [...shpFiles].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      });

      // Find the first file with valid esriUrl
      const validFile = sortedFiles.find(
        (file) => file.esriUrl && file.esriError?.toLowerCase() === 'success',
      );

      if (validFile) {
        setSelectedShpFile(validFile._id);
        const existingGroup = lasMapInstanceRef.current?.findLayerById(validFile._id) as __esri.GroupLayer;
        if (existingGroup) {
          // Hydrate sidebar from already-loaded map layer — don't add a duplicate
          const layersList = (existingGroup.layers as any).toArray().map((layer: any) => ({
            id: (layer as any).layerId?.toString() || '0',
            name: (layer as any).layerName || layer.title,
            url: layer.url,
            geometryType: (layer as any).geometryType,
            color: (layer as any).layerColor || '#0078D4',
            layer,
          }));
          setShapefileLayersList(layersList);
          const initialLayers: Record<string, boolean> = {};
          layersList.forEach((l: any) => { initialLayers[l.id] = false; });
          setShapefileLayers(initialLayers);
          setShapefileChecked(false);
          existingGroup.visible = false;
          (existingGroup.layers as any).forEach((layer: any) => { layer.visible = false; });
          if (!shpRef.current.some((l) => l.id === existingGroup.id)) {
            shpRef.current = [...shpRef.current, existingGroup];
          }
        } else {
          loadShapefileLayers(validFile.esriUrl, validFile._id);
        }
      } else {
        // No valid files, select the most recent one anyway (will show error)
        setSelectedShpFile(sortedFiles[0]._id);
      }
    }
  }, [shpFiles, selectedShpFile]);

  useEffect(() => {
    // Reset Geodatabase selection when order changes
    if (geoDbFiles.length > 0) {
      const sortedFiles = [...geoDbFiles].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      });

      // Find the first file with valid esriUrl
      const validFile = sortedFiles.find(
        (file) => file.esriUrl && file.esriError?.toLowerCase() === 'success',
      );

      if (validFile) {
        setSelectedGeoDbFile(validFile._id);
        loadGeodatabaseLayers(validFile.esriUrl, validFile._id);
      } else {
        // No valid files, select the most recent one anyway (will show error)
        setSelectedGeoDbFile(sortedFiles[0]._id);
        setGeodatabaseLayersList([]);
        setGeodatabaseLayers({});
        setGeodatabaseChecked(false);
      }
    } else {
      setSelectedGeoDbFile(null);
      setGeodatabaseLayersList([]);
    }

    // Reset Shapefile selection when order changes
    if (shpFiles.length > 0) {
      const sortedFiles = [...shpFiles].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      });

      // Find the first file with valid esriUrl
      const validFile = sortedFiles.find(
        (file) => file.esriUrl && file.esriError?.toLowerCase() === 'success',
      );

      if (validFile) {
        setSelectedShpFile(validFile._id);
        loadShapefileLayers(validFile.esriUrl, validFile._id);
      } else {
        // No valid files, select the most recent one anyway (will show error)
        setSelectedShpFile(sortedFiles[0]._id);
        setShapefileLayersList([]);
        setShapefileLayers({});
        setShapefileChecked(false);
      }
    } else {
      setSelectedShpFile(null);
      setShapefileLayersList([]);
    }
  }, [selectedOrderId]);

  const extractColorFromRenderer = (drawingInfo: any): string => {
    try {
      if (!drawingInfo || !drawingInfo.renderer) {
        return '#0078D4'; // Default ArcGIS blue
      }

      const { renderer } = drawingInfo;

      // Simple Renderer
      if (renderer.type === 'simple' && renderer.symbol) {
        const { symbol } = renderer;

        // For point symbols
        if (symbol.color && Array.isArray(symbol.color)) {
          const [r, g, b, a = 255] = symbol.color;
          return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
        }

        // For line symbols
        if (symbol.outline && symbol.outline.color && Array.isArray(symbol.outline.color)) {
          const [r, g, b, a = 255] = symbol.outline.color;
          return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
        }
      }

      // Unique Value Renderer - use first unique value's color
      if (renderer.type === 'uniqueValue' && renderer.uniqueValueInfos && renderer.uniqueValueInfos.length > 0) {
        const firstInfo = renderer.uniqueValueInfos[0];
        if (firstInfo.symbol && firstInfo.symbol.color && Array.isArray(firstInfo.symbol.color)) {
          const [r, g, b, a = 255] = firstInfo.symbol.color;
          return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
        }
      }

      // Class Breaks Renderer - use first class break's color
      if (renderer.type === 'classBreaks' && renderer.classBreakInfos && renderer.classBreakInfos.length > 0) {
        const firstInfo = renderer.classBreakInfos[0];
        if (firstInfo.symbol && firstInfo.symbol.color && Array.isArray(firstInfo.symbol.color)) {
          const [r, g, b, a = 255] = firstInfo.symbol.color;
          return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
        }
      }

      return '#0078D4'; // Default color if no valid color found
    } catch (err) {
      console.warn('Error extracting color from renderer:', err);
      return '#0078D4';
    }
  };

  // Load Geodatabase layers from FeatureServer
  const loadGeodatabaseLayers = async (esriUrl: Record<string, string>[], fileId: string) => {
    try {
      const sublayers: GeoJSONLayer[] = [];
      const layersList: any[] = [];

      for (let i = 0; i < esriUrl.length; i++) {
        const [layerName, geojsonUrl] = Object.entries(esriUrl[i])[0];

        try {
          let renderer: any;

          // Check explicitly defined styles inside styleConfig if layer name exact matches
          const layerConfig = styleConfig.layers[layerName];

          // Holds color specifically intended for mapping React UI sidebar list items
          let layerColorString: string | undefined;

          // Apply mapped config instantly if defined in imported styleConfig
          if (layerConfig) {
            renderer = getRendererForConfig(layerConfig, layerConfig.geometryType, (c) => { layerColorString = c; });
          }

          const layerProps: any = {
            url: GetEsriGeoJsonUrl(geojsonUrl),
            title: `${layerName}`,
            outFields: ['*'],
            visible: true,
          };

          // Add active configuration renderer to GeoJSONLayer class parameters
          if (renderer) {
            layerProps.renderer = renderer;
          }

          const layer = new GeoJSONLayer(layerProps);

          (layer as any).fileId = fileId;
          (layer as any).ownerOrder = selectedOrderId;
          (layer as any).layerId = i;
          (layer as any).layerName = layerName;

          if (layerColorString) {
            (layer as any).layerColor = layerColorString;
          }

          sublayers.push(layer);
          const listEntry = {
            id: String(i),
            name: layerName,
            url: GetEsriGeoJsonUrl(geojsonUrl),
            geometryType: null as any,
            color: layerColorString,
            layer,
          };
          layersList.push(listEntry);

          // Run once Esri has loaded the layer so we know its true geometryType
          // and fields. Renderer rules (data-driven) win over styleConfig (name-
          // driven), which wins over the geometry-type default.
          layer.when(() => {
            const rule = findRule({
              name: layerName,
              geometryType: layer.geometryType,
              fields: layer.fields ?? [],
            });

            if (rule) {
              rule.apply(layer);
              (layer as any).layerColor = rule.legendColor;
              listEntry.color = rule.legendColor;
              listEntry.geometryType = layer.geometryType as any;
              setGeodatabaseLayersList((prev: any[]) => [...prev]);
              return;
            }

            if (!layerConfig) {
              let defConfig;
              if (layer.geometryType === 'point' || layer.geometryType === 'multipoint') defConfig = styleConfig.defaults.point;
              else if (layer.geometryType === 'polyline') defConfig = styleConfig.defaults.polyline;
              else if (layer.geometryType === 'polygon') defConfig = styleConfig.defaults.polygon;

              if (defConfig) {
                const defRenderer = getRendererForConfig(defConfig, layer.geometryType);
                if (defRenderer) {
                  layer.renderer = defRenderer;
                }
                const fallbackColor = defConfig.symbol?.color;

                (layer as any).layerColor = fallbackColor;
                listEntry.color = fallbackColor;
                listEntry.geometryType = layer.geometryType as any;
                setGeodatabaseLayersList((prev: any[]) => [...prev]);
              }
            }
          }).catch(console.error);
        } catch (layerErr) {
          console.error(`Error creating Geodatabase layer "${layerName}":`, layerErr);
        }
      }

      const groupLayer = new GroupLayer({
        id: fileId,
        title: `Geodatabase_${fileId}`,
        layers: sublayers,
        visible: true,
      });
      (groupLayer as any).fileId = fileId;
      (groupLayer as any).ownerOrder = selectedOrderId;

      // Add the new group layer to the map — but only if it isn't already there
      if (lasMapInstanceRef.current) {
        const existingLayer = lasMapInstanceRef.current.findLayerById(fileId);
        if (!existingLayer) {
          lasMapInstanceRef.current.add(groupLayer as any);
          gdbRef.current = [...gdbRef.current, groupLayer as any];
        } else {
          // If already on map, ensure it's in our ref if not already
          if (!gdbRef.current.some((l) => l.id === fileId)) {
            gdbRef.current = [...gdbRef.current, existingLayer as any];
          }
        }
        (window as any).geodatabaseLayers = gdbRef.current;
      }

      setGeodatabaseLayersList(layersList);

      const initialLayers: Record<string, boolean> = {};
      layersList.forEach((l: any) => { initialLayers[l.id] = true; });
      setGeodatabaseLayers(initialLayers);
      setGeodatabaseChecked(true);
    } catch (error) {
      console.error('Error loading Geodatabase layers:', error);
    }
  };

  // Load Shapefile layers from FeatureServer
  const loadShapefileLayers = async (esriUrl: Record<string, string>[], fileId: string) => {
    try {
      const sublayers: GeoJSONLayer[] = [];
      const layersList: any[] = [];

      for (let i = 0; i < esriUrl.length; i++) {
        const [layerName, geojsonUrl] = Object.entries(esriUrl[i])[0];

        try {
          let renderer: any;

          // Check explicitly defined styles inside styleConfig if layer name exact matches
          const layerConfig = styleConfig.layers[layerName];

          // Holds color specifically intended for mapping React UI sidebar list items
          let layerColorString: string | undefined;

          // Apply mapped config instantly if defined in imported styleConfig
          if (layerConfig) {
            renderer = getRendererForConfig(layerConfig, layerConfig.geometryType, (c) => { layerColorString = c; });
          }

          const layerProps: any = {
            url: GetEsriGeoJsonUrl(geojsonUrl),
            title: `${layerName}`,
            outFields: ['*'],
            visible: false,
          };

          // Add active configuration renderer to GeoJSONLayer class parameters
          if (renderer) {
            layerProps.renderer = renderer;
          }

          const layer = new GeoJSONLayer(layerProps);

          (layer as any).fileId = fileId;
          (layer as any).ownerOrder = selectedOrderId;
          (layer as any).layerId = i;
          (layer as any).layerName = layerName;

          if (layerColorString) {
            (layer as any).layerColor = layerColorString;
          }

          sublayers.push(layer);
          const listEntry = {
            id: String(i),
            name: layerName,
            url: GetEsriGeoJsonUrl(geojsonUrl),
            geometryType: null as any,
            color: layerColorString,
            layer,
          };
          layersList.push(listEntry);

          // Execute assignment inside .when() to ensure Esri has loaded exact geometry properties
          if (!layerConfig) {
            layer.when(() => {
              let defConfig;
              // Map rendering uses full default styles matching recognized data topography natively
              if (layer.geometryType === 'point' || layer.geometryType === 'multipoint') defConfig = styleConfig.defaults.point;
              else if (layer.geometryType === 'polyline') defConfig = styleConfig.defaults.polyline;
              else if (layer.geometryType === 'polygon') defConfig = styleConfig.defaults.polygon;

              if (defConfig) {
                const defRenderer = getRendererForConfig(defConfig, layer.geometryType);
                if (defRenderer) {
                  layer.renderer = defRenderer;
                }
                const fallbackColor = defConfig.symbol?.color;

                (layer as any).layerColor = fallbackColor;
                listEntry.color = fallbackColor;
                listEntry.geometryType = layer.geometryType as any;

                // Triggers structural re-render to update the box colors.
                setShapefileLayersList((prev: any[]) => [...prev]);
              }
            }).catch(console.error);
          }
        } catch (layerErr) {
          console.error(`Error creating Shapefile layer "${layerName}":`, layerErr);
        }
      }

      const groupLayer = new GroupLayer({
        id: fileId,
        title: `Shapefile_${fileId}`,
        layers: sublayers,
        visible: false,
      });
      (groupLayer as any).fileId = fileId;
      (groupLayer as any).ownerOrder = selectedOrderId;

      // Add the new group layer to the map — but only if it isn't already there
      if (lasMapInstanceRef.current) {
        const existingLayer = lasMapInstanceRef.current.findLayerById(fileId);
        if (!existingLayer) {
          lasMapInstanceRef.current.add(groupLayer as any);
          shpRef.current = [...shpRef.current, groupLayer as any];
        } else {
          // If already on map, ensure it's in our ref if not already
          if (!shpRef.current.some((l) => l.id === fileId)) {
            shpRef.current = [...shpRef.current, existingLayer as any];
          }
        }
        (window as any).shapefileLayers = shpRef.current;
      }

      setShapefileLayersList(layersList);

      const initialLayers: Record<string, boolean> = {};
      layersList.forEach((l: any) => { initialLayers[l.id] = false; });
      setShapefileLayers(initialLayers);
      setShapefileChecked(false);
    } catch (error) {
      console.error('Error loading Shapefile layers:', error);
    }
  };

  // Geodatabase file change handler
  const handleGeodatabaseFileChange = async (e: any) => {
    const fileId = e.target.value;
    setSelectedGeoDbFile(fileId);

    const file = geoDbFiles.find((f: IGeoDbFile) => f._id === fileId);
    // Tell Redux which gdb VERSION the user selected — this drives everything version-scoped:
    // the Khora chat targets THIS gdb (gdb_id), the chat session swaps to THIS version's own
    // thread (sessionKey), and org-shared pins scope to it. Selection-based on purpose: it
    // follows the user's choice even if the version's published layers fail to render below.
    if (file) dispatch(setLoadedGdbId(String(fileId)));

    // Hide ALL existing Geodatabase groups on the map (only hide, don't remove – they may be re-used)
    if (lasMapInstanceRef.current) {
      lasMapInstanceRef.current.layers.forEach((layer) => {
        if (layer.type === 'group' && (layer as any).fileId) {
          const isGdb = gdbRef.current.some((gl) => gl.id === layer.id) ||
            geoDbFiles.some((f) => f._id === (layer as any).fileId);
          if (isGdb) layer.visible = false;
        }
      });
    }

    if (!file?.esriUrl || file.esriError?.toLowerCase() !== 'success') {
      console.warn('Switching Geodatabase file - No valid URL for file:', fileId);
      setGeodatabaseLayersList([]);
      setGeodatabaseLayers({});
      setGeodatabaseChecked(false);
      return;
    }

    // Check if group for this file already exists on the map
    const mapGroup = lasMapInstanceRef.current?.findLayerById(fileId) as __esri.GroupLayer;

    if (mapGroup) {
      // Layer group already in map — just show it and update the sidebar list
      const layersList = mapGroup.layers.map((layer: any) => {
        const layerName = (layer as any).layerName || layer.title?.split('_').slice(1).join('_') || layer.Title;
        const layerId = (layer as any).layerId?.toString() || '0';

        let layerColor = (layer as any).layerColor || (layer as any).color;

        // Renderer rule wins over styleConfig and default fallback — must run
        // in this cached-toggle path too, otherwise switching back to a
        // previously-loaded file resets the 3D cylinder (etc.) to the 2D
        // marker from styleConfig. Layer is already loaded so fields and
        // geometryType are populated synchronously.
        const rule = findRule({
          name: layerName,
          geometryType: layer.geometryType,
          fields: layer.fields ?? [],
        });

        if (rule) {
          rule.apply(layer);
          (layer as any).layerColor = rule.legendColor;
          layerColor = rule.legendColor;
        } else {
          const layerConfig = styleConfig.layers[layerName];
          if (layerConfig) {
            const renderer = getRendererForConfig(layerConfig, layerConfig.geometryType, (c) => { layerColor = c; });
            if (renderer) {
              layer.renderer = renderer;
              (layer as any).layerColor = layerColor;
            }
          } else {
            // Fallback styling for layers without specific config
            layer.when(() => {
              let defConfig;
              if (layer.geometryType === 'point' || layer.geometryType === 'multipoint') defConfig = styleConfig.defaults.point;
              else if (layer.geometryType === 'polyline') defConfig = styleConfig.defaults.polyline;
              else if (layer.geometryType === 'polygon') defConfig = styleConfig.defaults.polygon;

              if (defConfig) {
                const defRenderer = getRendererForConfig(defConfig, layer.geometryType);
                if (defRenderer) {
                  layer.renderer = defRenderer;
                }
                const fallbackColor = defConfig.symbol?.color || '#0078D4';
                (layer as any).layerColor = fallbackColor;
                // Note: We might want to trigger a list update here too if color changes
              }
            });
          }
        }

        return {
          id: layerId,
          name: layerName,
          url: layer.url,
          geometryType: (layer as any).geometryType,
          color: layerColor,
          layer,
        };
      }).toArray();

      setGeodatabaseLayersList(layersList);

      const initialLayers: Record<string, boolean> = {};
      mapGroup.layers.forEach((layer: any) => {
        const layerId = (layer as any).layerId?.toString() || '0';
        initialLayers[layerId] = true;
        layer.visible = true;
      });

      mapGroup.visible = true;
      setGeodatabaseLayers(initialLayers);
      setGeodatabaseChecked(true);
    } else {
      // New file never loaded — loadGeodatabaseLayers will create and ADD it to the map
      await loadGeodatabaseLayers(file.esriUrl, fileId);
    }
  };

  // Shapefile file change handler
  const handleShapefileFileChange = async (e: any) => {
    const fileId = e.target.value;
    setSelectedShpFile(fileId);

    const file = shpFiles.find((f: any) => f._id === fileId);

    // Hide ALL existing shapefile groups on the map
    if (lasMapInstanceRef.current) {
      lasMapInstanceRef.current.layers.forEach((layer) => {
        if (layer.type === 'group' && (layer as any).fileId) {
          // Verify it's a shapefile group
          const isShp = shpRef.current.some((sl) => sl.id === layer.id);
          shpFiles.some((f) => f._id === (layer as any).fileId);
          if (isShp) layer.visible = false;
        }
      });
    }

    if (!file?.esriUrl || file.esriError?.toLowerCase() !== 'success') {
      console.warn('Switching SHP file - No valid URL for file:', fileId);
      setShapefileLayersList([]);
      setShapefileLayers({});
      setShapefileChecked(false);
      return;
    }

    // Check if group for this file already exists on the map
    const mapGroup = lasMapInstanceRef.current?.findLayerById(fileId) as __esri.GroupLayer;

    if (mapGroup) {
      // Layer group already in map — just show it and update the sidebar list
      const layersList = mapGroup.layers.map((layer: any) => {
        const layerName = (layer as any).layerName || layer.title?.split('_').slice(1).join('_') || layer.Title;
        const layerId = (layer as any).layerId?.toString() || '0';

        let layerColor = (layer as any).layerColor || (layer as any).color;

        // Renderer rule wins over styleConfig and default fallback — must run
        // in this cached-toggle path too, otherwise switching back to a
        // previously-loaded file resets the 3D cylinder (etc.) to the 2D
        // marker from styleConfig. Layer is already loaded so fields and
        // geometryType are populated synchronously.
        const rule = findRule({
          name: layerName,
          geometryType: layer.geometryType,
          fields: layer.fields ?? [],
        });

        if (rule) {
          rule.apply(layer);
          (layer as any).layerColor = rule.legendColor;
          layerColor = rule.legendColor;
        } else {
          const layerConfig = styleConfig.layers[layerName];
          if (layerConfig) {
            const renderer = getRendererForConfig(layerConfig, layerConfig.geometryType, (c) => { layerColor = c; });
            if (renderer) {
              layer.renderer = renderer;
              (layer as any).layerColor = layerColor;
            }
          } else {
            // Fallback styling for layers without specific config
            layer.when(() => {
              let defConfig;
              if (layer.geometryType === 'point' || layer.geometryType === 'multipoint') defConfig = styleConfig.defaults.point;
              else if (layer.geometryType === 'polyline') defConfig = styleConfig.defaults.polyline;
              else if (layer.geometryType === 'polygon') defConfig = styleConfig.defaults.polygon;

              if (defConfig) {
                const defRenderer = getRendererForConfig(defConfig, layer.geometryType);
                if (defRenderer) {
                  layer.renderer = defRenderer;
                }
                const fallbackColor = defConfig.symbol?.color || '#0078D4';
                (layer as any).layerColor = fallbackColor;
                // Note: We might want to trigger a list update here too if color changes
              }
            });
          }
        }

        return {
          id: layerId,
          name: layerName,
          url: layer.url,
          geometryType: (layer as any).geometryType,
          color: layerColor,
          layer,
        };
      }).toArray();

      setShapefileLayersList(layersList);

      const initialLayers: Record<string, boolean> = {};
      mapGroup.layers.forEach((layer: any) => {
        const layerId = (layer as any).layerId?.toString() || '0';
        initialLayers[layerId] = true;
        layer.visible = true;
      });

      mapGroup.visible = true;
      setShapefileLayers(initialLayers);
      setShapefileChecked(true);
    } else {
      // New file never loaded — loadShapefileLayers will create and ADD it to the map
      await loadShapefileLayers(file.esriUrl, fileId);
    }
  };

  // Geodatabase master toggle
  const handleGeodatabaseMasterToggle = () => {
    const newState = !geodatabaseChecked;
    setGeodatabaseChecked(newState);

    // Update state first
    const newLayers: Record<string, boolean> = {};
    geodatabaseLayersList.forEach((layerInfo) => {
      newLayers[layerInfo.id] = newState;
    });
    setGeodatabaseLayers(newLayers);

    // Toggle GroupLayer and its sublayers visibility in the map
    if (lasMapInstanceRef.current && selectedGeoDbFile) {
      const groupLayer = lasMapInstanceRef.current.findLayerById(selectedGeoDbFile) as __esri.GroupLayer;
      if (groupLayer) {
        groupLayer.visible = newState;
        // Also update all sublayers visibility
        groupLayer.layers.forEach((layer) => {
          layer.visible = newState;
        });
      } else {
        console.warn('Master Toggle - could not find GroupLayer for ID:', selectedGeoDbFile);
      }
    } else {
      console.warn('Master Toggle - Map or selectedGeoDbFile missing');
    }
  };

  // Geodatabase layer toggle
  const handleGeodatabaseLayerToggle = (layerId: string) => {
    const newState = !geodatabaseLayers[layerId];

    setGeodatabaseLayers((prev) => {
      const updated = { ...prev, [layerId]: newState };

      // Check if all layers are visible to update master checkbox
      const allVisible = Object.values(updated).length > 0 && Object.values(updated).every((v) => v);
      setGeodatabaseChecked(allVisible);

      return updated;
    });

    // Update actual layer visibility in the GroupLayer
    if (lasMapInstanceRef.current && selectedGeoDbFile) {
      const groupLayer = lasMapInstanceRef.current.findLayerById(selectedGeoDbFile) as __esri.GroupLayer;
      if (groupLayer) {
        const sublayer = groupLayer.layers.find((l: any) => l.layerId?.toString() === layerId);
        if (sublayer) {
          sublayer.visible = newState;

          // Sync GroupLayer visibility: if any sublayer is on, the group must be on
          if (newState) {
            groupLayer.visible = true;
          } else {
            // If turning off, check if it was the last visible sublayer
            const anyOtherVisible = groupLayer.layers.some(
              (l: any) => (l as any).layerId?.toString() !== layerId && l.visible,
            );
            if (!anyOtherVisible) {
              groupLayer.visible = false;
            }
          }
        } else {
          console.warn('Sublayer Toggle - could not find sublayer for layerId:', layerId);
        }
      } else {
        console.warn('Sublayer Toggle - could not find GroupLayer for ID:', selectedGeoDbFile);
      }
    }
  };

  // Shapefile master toggle
  const handleShapefileMasterToggle = () => {
    const newState = !shapefileChecked;
    setShapefileChecked(newState);

    // Update state first
    const newLayers: Record<string, boolean> = {};
    shapefileLayersList.forEach((layerInfo) => {
      newLayers[layerInfo.id] = newState;
    });
    setShapefileLayers(newLayers);

    // Toggle GroupLayer and its sublayers visibility in the map
    if (lasMapInstanceRef.current && selectedShpFile) {
      const groupLayer = lasMapInstanceRef.current.findLayerById(selectedShpFile) as __esri.GroupLayer;
      if (groupLayer) {
        groupLayer.visible = newState;
        // Also update all sublayers visibility
        groupLayer.layers.forEach((layer) => {
          layer.visible = newState;
        });
      } else {
        console.warn('SHP Master Toggle - could not find GroupLayer for ID:', selectedShpFile);
      }
    } else {
      console.warn('SHP Master Toggle - Map or selectedShpFile missing');
    }
  };

  // Shapefile layer toggle
  const handleShapefileLayerToggle = (layerId: string) => {
    const newState = !shapefileLayers[layerId];

    setShapefileLayers((prev) => {
      const updated = { ...prev, [layerId]: newState };

      // Check if all layers are visible to update master checkbox
      const allVisible = Object.values(updated).length > 0 && Object.values(updated).every((v) => v);
      setShapefileChecked(allVisible);

      return updated;
    });

    // Update actual layer visibility in the GroupLayer
    if (lasMapInstanceRef.current && selectedShpFile) {
      const groupLayer = lasMapInstanceRef.current.findLayerById(selectedShpFile) as __esri.GroupLayer;
      if (groupLayer) {
        const sublayer = groupLayer.layers.find((l: any) => l.layerId?.toString() === layerId);
        if (sublayer) {
          sublayer.visible = newState;

          // Sync GroupLayer visibility: if any sublayer is on, the group must be on
          if (newState) {
            groupLayer.visible = true;
          } else {
            // If turning off, check if it was the last visible sublayer
            const anyOtherVisible = groupLayer.layers.some(
              (l: any) => (l as any).layerId?.toString() !== layerId && l.visible,
            );
            if (!anyOtherVisible) {
              groupLayer.visible = false;
            }
          }
        } else {
          console.warn('SHP Sublayer Toggle - could not find sublayer for layerId:', layerId);
        }
      } else {
        console.warn('SHP Sublayer Toggle - could not find GroupLayer for ID:', selectedShpFile);
      }
    }
  };

  // Get filename from full path
  const getFileName = (filepath: string): string => filepath.substring(filepath.lastIndexOf('/') + 1);

  // Check if file has error
  const hasFileError = (file: IGeoDbFile | IShpFile): boolean => !!file.esriError && file.esriError.toLowerCase() !== 'success';

  // Render layer list with eye toggles
  const renderLayerList = (
    layers: any[],
    checkedLayers: Record<string, boolean>,
    onToggle: (layerId: string) => void,
    showAll: boolean,
    limitTo: number = 5, // limiting number of layers visible in the section initially
  ) => {
    const layersToRender = showAll ? layers : layers.slice(0, limitTo);

    return (
      <Box sx={styles.layerListContainer}>
        {layersToRender.map((layer) => (
          <Box key={layer.id} sx={styles.layerItem}>
            {/* Color indicator - using default color since we don't have renderer info */}
            <Box
              sx={{
                ...styles.layerColorBox,
                backgroundColor: layer.color || '#0078D4', // Default ArcGIS blue
              }}
            />

            {/* Layer name */}
            <Typography variant="body2" sx={styles.layerNameText}>
              {layer.name}
            </Typography>

            {/* Eye toggle */}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggle(layer.id);
              }}
              sx={styles.eyetogglelegend}
            >
              <SvgIcon fontSize="small">
                {checkedLayers[layer.id] ? (
                  // eye-open
                  <path d="M10 4.1665C5.90909 4.1665 2.41545 6.71105 1 10.3029C2.41545 13.8947 5.90909 16.4392 10 16.4392C14.0909 16.4392 17.5845 13.8947 19 10.3029C17.5845 6.71105 14.0909 4.1665 10 4.1665ZM10 14.3938C7.74182 14.3938 5.90909 12.561 5.90909 10.3029C5.90909 8.04469 7.74182 6.21196 10 6.21196C12.2582 6.21196 14.0909 8.04469 14.0909 10.3029C14.0909 12.561 12.2582 14.3938 10 14.3938ZM10 7.84832C8.64182 7.84832 7.54545 8.94469 7.54545 10.3029C7.54545 11.661 8.64182 12.7574 10 12.7574C11.3582 12.7574 12.4545 11.661 12.4545 10.3029C12.4545 8.94469 11.3582 7.84832 10 7.84832Z" />
                ) : (
                  // eye-closed
                  <path d="M19.5872 1.06055L15.0764 5.57031C16.7934 6.66308 18.1433 8.283 18.8997 10.2021C17.4842 13.7939 13.9905 16.3388 9.89966 16.3389C8.27973 16.3389 6.754 15.9384 5.41333 15.2334L1.06079 19.5869L0.212158 18.7383L4.35864 14.5908C2.81491 13.5073 1.60167 11.9836 0.899658 10.2021C2.31516 6.61043 5.80882 4.06641 9.89966 4.06641C11.3582 4.06643 12.7402 4.39068 13.9797 4.96973L18.7385 0.211914L19.5872 1.06055ZM11.885 8.76172C12.1787 9.16626 12.3537 9.66269 12.3538 10.2021C12.3538 11.5603 11.2578 12.6572 9.89966 12.6572C9.36009 12.6572 8.86285 12.4822 8.45825 12.1885L7.29321 13.3535C8.00122 13.9399 8.90925 14.293 9.89966 14.293C12.1578 14.2929 13.9905 12.4603 13.9905 10.2021C13.9904 9.21179 13.6374 8.30367 13.051 7.5957L11.885 8.76172ZM9.89966 6.11133C7.64153 6.11133 5.80892 7.94404 5.80884 10.2021C5.80884 11.0367 6.0599 11.8127 6.4895 12.46L7.68677 11.2627C7.53306 10.9417 7.44458 10.5826 7.44458 10.2021C7.44467 8.84404 8.54153 7.74805 9.89966 7.74805C10.2799 7.74807 10.6383 7.83663 10.9592 7.99023L12.1575 6.79199C11.5102 6.36239 10.7342 6.11136 9.89966 6.11133Z" />
                )}
              </SvgIcon>
            </IconButton>
          </Box>
        ))}
      </Box>
    );
  };

  useEffect(() => {
    setShowAllClassesByFile({});
    setLastClassVisibilityState({});
  }, [orderSelected]);

  useEffect(() => {
    if (!orderSelected && sortedPlacedOrders.length > 0) {
      // Find the first order that has either CAD files or classified point clouds
      const firstOrderWithData = sortedPlacedOrders.find((order) => {
        const hasCadFiles = order.cadFiles && order.cadFiles.length > 0;
        const hasClassifiedPointClouds = lasFilesWith3DTile.some(
          (las) => las.lasType !== 'unclassified' && las.ownerOrder === order._id,
        );
        return hasCadFiles || hasClassifiedPointClouds;
      });

      // found an order with data, use it; otherwise fall back to first order
      const targetOrder = firstOrderWithData || sortedPlacedOrders[0];

      dispatch(SetOrderSelectedAction(targetOrder._id));
    }
  }, [orderSelected, sortedPlacedOrders, dispatch, lasFilesWith3DTile]);

  const handleToggleSidebar = () => {
    onToggleSidebar(!isSidebarVisible);
  };

  const getLasTitle = (las: ILasFile): string => {
    const fullPath = las.filename || '';
    const filename =
      fullPath.substring(fullPath.lastIndexOf('/') + 1) ||
      las.lasFilename ||
      `PointCloud-${projectData.lasFiles?.indexOf(las) ?? 0}`;
    return filename.replace(/\.(las|laz)$/, '');
  };

  const unclassifiedLas = inputLas.filter((las) => las.lasType === 'unclassified');
  // CLASSIFIED POINT CLOUD – filter by ownerOrder
  const classifiedLas = inputLas.filter(
    (las) => las.lasType !== 'unclassified' && las.ownerOrder === selectedOrderId,
  );

  // Get the most recent classified LAS file based on createdAt
  const recentclassifiedLas = classifiedLas.length > 0
    ? [classifiedLas.reduce((latest, current) => {
      const currentDate = new Date((projectData.lasFiles?.find((f: any) => f._id === current._id) as any)?.createdAt || 0).getTime();
      const latestDate = new Date((projectData.lasFiles?.find((f: any) => f._id === latest._id) as any)?.createdAt || 0).getTime();
      return currentDate > latestDate ? current : latest;
    })]
    : [];

  const hasClassifiedPointClouds = recentclassifiedLas.length > 0;
  const hasUnclassifiedPointClouds = unclassifiedLas.length > 0;

  const allClassesVisible =
    hasClassifiedPointClouds &&
    recentclassifiedLas.every((las) => {
      const title = getLasTitle(las);
      const vis = classVisibility[title];
      return vis && Object.values(vis).every(Boolean);
    });

  const someClassesVisible =
    hasClassifiedPointClouds &&
    recentclassifiedLas.some((las) => {
      const title = getLasTitle(las);
      const vis = classVisibility[title];
      return vis && Object.values(vis).some(Boolean);
    });

  // CAD –  filtered by the selector array
  const cadFilesForOrder = selectedOrder?.cadFiles ?? [];

  // Feet-per-metre for the elevation-offset input — follows the order's own CAD deliverables (US
  // survey foot vs international foot) rather than assuming one.
  const feetPerMetre = useMemo(
    () => resolveFeetPerMetre(resolveOrderCadUnits(cadFilesForOrder)),
    [cadFilesForOrder],
  );

  // Which unit the elevation-offset input is shown in. Metres is the value's native/stored unit;
  // feet is a display convenience layered on top via feetPerMetre.
  const [displayUnit, setDisplayUnit] = useState<DisplayUnit>('m');
  const handleDisplayUnitChange = (event: SelectChangeEvent<DisplayUnit>) => {
    setDisplayUnit(event.target.value as DisplayUnit);
  };

  // Draft of the elevation-offset input, shown and edited in whichever unit is selected;
  // committed (converted back to metres, which rebuilds the terrain and saves against the loaded
  // geodatabase) only on blur/Enter so typing doesn't reload the ground on every keystroke.
  const [offsetDraft, setOffsetDraft] = useState<string>(String(userElevationOffset ?? 0));

  const commitOffset = () => {
    // What the field would show if untouched since the last resync — comparing the draft string
    // against this, rather than a numeric round-trip through the unit conversion, is what tells a
    // genuine edit apart from an unchanged field. A metres<->feet round-trip is not perfectly
    // reversible in floating point, so comparing converted numbers would occasionally treat an
    // untouched field as "changed" and quietly shave precision off the stored value on every blur.
    const synced = formatOffsetDraft(userElevationOffset, displayUnit, feetPerMetre);
    // An unchanged field is nothing to save, unless the last save failed: the value shown is already
    // the one the user typed, so re-entering it is the retry the message asks for.
    if (offsetDraft === synced && !elevationSaveFailed) return;
    const nextMetres = parseOffsetDraftToMetres(offsetDraft, displayUnit, feetPerMetre);
    if (nextMetres === null) {
      // Not a number at all (e.g. mid-typo) — revert rather than leave unparseable text sitting in
      // the field indefinitely with no indication the edit was rejected.
      setOffsetDraft(synced);
      return;
    }
    onCommitUserElevationOffset(nextMetres);
  };

  // Resyncs on mount, on a gdb switch, and on a unit toggle — without this the field would keep
  // showing the previous geodatabase's number, or the previous unit's number, after either
  // changes. selectedGeoDbFile is a dependency alongside userElevationOffset because two
  // geodatabases can resolve to the same value (most commonly both unset, both 0): depending on
  // the value alone would then skip the resync on a switch between them, leaving an uncommitted
  // draft from the OLD geodatabase in the field to be silently saved against the NEW one on blur.
  useEffect(() => {
    setOffsetDraft(formatOffsetDraft(userElevationOffset, displayUnit, feetPerMetre));
  }, [userElevationOffset, displayUnit, feetPerMetre, selectedGeoDbFile]);

  const handleOrderChange = (newOrderId: string) => {
    dispatch(SetOrderSelectedAction(newOrderId));

    const selectedOrder = placedOrders.find((o) => o._id === newOrderId);
    if (!selectedOrder) return;

    // Update CAD layers for the new order
    const cadIdsForNewOrder = (selectedOrder.cadFiles ?? []).map((c: any) => c._id);

    // Clear existing definitions and apply new ones
    cadIdsForNewOrder.forEach((cadId: string) => {
      if ((window as any).setLayerDefinitions) {
        (window as any).setLayerDefinitions(cadId);
      }
      if (selectedOrder.orderStatus === orderStatusCode.done) {
        if ((window as any).queryLayersTable) {
          (window as any).queryLayersTable(cadId);
        }
      }
    });
    if (lasFilesWith3DTile && lasFilesWith3DTile.length > 0) {
      lasFilesWith3DTile.forEach((las) => {
        // Skip unclassified point clouds - they should not be affected by order changes
        if (las.lasType === 'unclassified') {
          return;
        }

        const title = getLasTitle(las);
        const shouldBeVisible = las.ownerOrder === newOrderId;
        if (shouldBeVisible) {
          // Always default to all classes visible when switching to this order
          setAllClassesVisibility(title, true);
        } else {
          // Hide point clouds from other orders
          toggleLasVisibility(title, false);
        }
      });
    }

    // Zoom to first CAD of the new order
    if (selectedOrder.cadFiles?.length > 0) {
      const firstCadFile = selectedOrder.cadFiles[0];
      setTimeout(() => {
        if ((window as any).zoomToSite) {
          (window as any).zoomToSite(firstCadFile._id);
        }
      }, 500);
    }
  };

  const hasPointCloudError = (las: ILasFile): boolean => !!las.esriError && las.esriError.toLowerCase() !== 'success';

  const MAX_TO_SHOW = 5; // limiting number of layers visible in the section initially

  useEffect(() => {
    if (unclassifiedLas.length > 0) {
      const allVisible = unclassifiedLas.every((las) => {
        const title = getLasTitle(las);
        return lasFilesDisplayStatuses[title] !== false;
      });
      setShowPointCloud(allVisible);
    }
  }, [lasFilesDisplayStatuses, unclassifiedLas]);

  const somePointCloudVisible = useMemo(() => unclassifiedLas.some((las) => {
    const title = getLasTitle(las);
    return lasFilesDisplayStatuses[title] !== false;
  }), [lasFilesDisplayStatuses, unclassifiedLas]);

  const handleErrorIconClick = (event: React.MouseEvent<HTMLElement>, fileId: string) => {
    setErrorTooltipAnchor(event.currentTarget);
    setErrorTooltipFileId(fileId);
  };

  const handleErrorTooltipClose = () => {
    setErrorTooltipAnchor(null);
    setErrorTooltipFileId(null);
  };

  return (
    <Box
      sx={{ ...styles.mainContent, ...styles.sidebarWrapper(isSidebarVisible) }}
    >
      <Sidebar data-test="sidebar-rendering">
        <Box sx={styles.mainContent}>
          <Box sx={{ ...styles.sidebarContent, ...styles.sidebarContainer }}>
            {isSidebarVisible && (
              <IconButton
                onClick={handleToggleSidebar}
                sx={styles.pinButton}
                aria-label={isSidebarVisible ? 'Hide Content' : 'Show Content'}
              >
                <SvgIcon sx={styles.togglesidebarsvg}>
                  <rect fill="none" width="24" height="24" />
                  <polygon
                    points="15.83,2.5 4.17,2.5 2.5,4.17 2.5,15.83 4.17,17.5 15.83,17.5 17.5,15.83 17.5,4.17"
                    fill="none"
                    stroke="#021E40"
                    strokeWidth="1.46"
                  />
                  <polygon
                    points="7.5,2.5 7.5,17.5"
                    fill="none"
                    stroke="#021E40"
                    strokeWidth="1.46"
                  />

                  <polygon
                    points="13.33,12.5 10.83,10 13.33,7.5"
                    fill="none"
                    stroke="#021E40"
                    strokeWidth="1.46"
                  />
                </SvgIcon>
              </IconButton>
            )}
            <Box sx={styles.orderchangebox}>
              {sortedPlacedOrders.length === 0 ? (
                // Show "NO ORDER PLACED" message when there are no orders
                <Box
                  sx={styles.noorderbox}
                >
                  <SvgIcon fontSize="small" viewBox="0 0 16 16">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.4001 8.0001C14.4001 11.5347 11.5347 14.4001 8.0001 14.4001C4.46548 14.4001 1.6001 11.5347 1.6001 8.0001C1.6001 4.46548 4.46548 1.6001 8.0001 1.6001C11.5347 1.6001 14.4001 4.46548 14.4001 8.0001ZM8.0001 4.0001C8.33147 4.0001 8.6001 4.26873 8.6001 4.6001V8.2001C8.6001 8.53147 8.33147 8.8001 8.0001 8.8001C7.66873 8.8001 7.4001 8.53147 7.4001 8.2001V4.6001C7.4001 4.26873 7.66873 4.0001 8.0001 4.0001ZM8.0001 12.0001C8.44193 12.0001 8.8001 11.6419 8.8001 11.2001C8.8001 10.7583 8.44193 10.4001 8.0001 10.4001C7.55827 10.4001 7.2001 10.7583 7.2001 11.2001C7.2001 11.6419 7.55827 12.0001 8.0001 12.0001Z"
                      fill="#8896A5"
                    />
                  </SvgIcon>
                  <Typography
                    sx={styles.noordertypo}
                  >
                    NO ORDER PLACED
                  </Typography>
                </Box>
              ) : (
                <Select
                  value={orderSelected ?? ''}
                  onChange={(e) => handleOrderChange(e.target.value as string)}
                  displayEmpty
                  size="small"
                  fullWidth
                  sx={styles.orderSelect}
                  renderValue={(selected) => {
                    if (!selected) return <em>NO ORDER PLACED</em>;
                    const idx = sortedPlacedOrders.findIndex((o) => o._id === selected);
                    if (idx === -1) return 'ORDER';
                    return `ORDER ${sortedPlacedOrders.length - idx}`;
                  }}
                >
                  {sortedPlacedOrders.map((order, idx) => (
                    <MenuItem key={order._id} value={order._id}>
                      ORDER
                      {' '}
                      {sortedPlacedOrders.length - idx}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Box>
            {/* Sidebar Content - Toggles Visibility */}
            {isSidebarVisible && (
              <Box sx={styles.filesSidebar}>
                {/* Elevation offset — the user's own vertical nudge on top of the order's automatic
                    geoid correction (it moves the ground, not the data), so 0 sits on the geoid
                    plane. The correction itself is explained behind the 'i' icon rather than its
                    own line — the number was redundant with what the icon's tooltip already says.
                    Edits are saved against the loaded geodatabase, always in metres — the unit
                    toggle only changes how the value is displayed/edited here. */}
                <Box sx={styles.elevationOffsetBox}>
                  <Box sx={styles.elevationOffsetHeaderRow}>
                    <Box sx={styles.elevationOffsetLabelGroup}>
                      <Typography sx={styles.elevationOffsetLabel}>Elevation Offset</Typography>
                      <ElevationOffsetIcon
                        geoidStatus={geoidStatus}
                        projectGeoidOffset={projectGeoidOffset}
                        pendingGeoidState={pendingGeoidState}
                        feetPerMetre={feetPerMetre}
                        displayUnit={displayUnit}
                      />
                    </Box>
                    <Select<DisplayUnit>
                      value={displayUnit}
                      onChange={handleDisplayUnitChange}
                      size="small"
                      variant="standard"
                      disableUnderline
                      sx={styles.elevationOffsetUnitSelect}
                      inputProps={{ 'aria-label': 'Elevation display units' }}
                    >
                      <MenuItem value="m">m</MenuItem>
                      <MenuItem value="ft">ft</MenuItem>
                    </Select>
                  </Box>
                  <TextField
                    type="number"
                    size="small"
                    value={offsetDraft}
                    onChange={(e) => setOffsetDraft(e.target.value)}
                    onBlur={commitOffset}
                    onKeyDown={(e) => { if (e.key === 'Enter') commitOffset(); }}
                    // Nothing to nudge before the order is placed: no geodatabase to save against.
                    disabled={geoidStatus === 'pending'}
                    inputProps={{ step: 1, 'aria-label': `Elevation offset in ${displayUnit === 'ft' ? 'feet' : 'metres'}` }}
                    sx={styles.elevationOffsetInput}
                  />
                  <ElevationOffsetInfo
                    geoidStatus={geoidStatus}
                    geoidUnavailableReason={geoidUnavailableReason}
                    pendingGeoidState={pendingGeoidState}
                    elevationSaveFailed={elevationSaveFailed}
                  />
                </Box>
                <Box sx={styles.sidebarTitle}>
                  <Typography display="inline" sx={styles.header} variant="h4">
                    {currentPanel}
                  </Typography>
                </Box>
                {/* --- 3D Layers Section --- */}
                <Box sx={styles.layerbox}>
                  {/* --- Orthomosaic Section --- */}
                  <Orthomosaic />
                  {/* ==================== GEODATABASE SECTION ==================== */}
                  <ListItemButton
                    sx={styles.expansionPanelHeader}
                    onClick={() => setGeodatabaseExpanded(!geodatabaseExpanded)}
                  >
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={geoDbFiles.length === 0 ? false : geodatabaseChecked}
                          disabled={geoDbFiles.length === 0}
                          indeterminate={
                            geoDbFiles.length > 0 &&
                            !geodatabaseChecked &&
                            Object.values(geodatabaseLayers).some((v) => v)
                          }
                          onChange={handleGeodatabaseMasterToggle}
                          onClick={(e) => e.stopPropagation()}
                          color="primary"
                        />
                      )}
                      label={(
                        <Typography variant="h5" sx={styles.layerName}>
                          Geodatabase
                        </Typography>
                      )}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <IconButton sx={styles.button}>
                      {geodatabaseExpanded ? (
                        <KeyboardArrowDownIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowRightIcon fontSize="small" />
                      )}
                    </IconButton>
                  </ListItemButton>

                  <Collapse in={geodatabaseExpanded}>
                    <Box sx={styles.wrapper}>
                      {geoDbFiles.length === 0 ? (
                        <Typography variant="body2" sx={styles.fileItalic}>
                          {placedOrders.length === 0 ? 'No Order Placed' : 'Data Coming Soon'}
                        </Typography>
                      ) : (
                        <>
                          <Select
                            value={selectedGeoDbFile || ''}
                            onChange={handleGeodatabaseFileChange}
                            displayEmpty
                            variant="standard"
                            disableUnderline
                            sx={styles.ongroundfilechange}
                            IconComponent={KeyboardArrowDownIcon}
                          >
                            {geoDbFiles.map((file: IGeoDbFile) => (
                              <MenuItem key={file._id} value={file._id}>
                                {getFileName(file.filename)}
                              </MenuItem>
                            ))}
                          </Select>

                          {/* Error Message */}
                          {selectedGeoDbFile && (() => {
                            const file = geoDbFiles.find((f: IGeoDbFile) => f._id === selectedGeoDbFile);
                            // Check if file has no esriUrl
                            if (file && !file.esriUrl) {
                              return (
                                <Typography variant="body2" sx={styles.fileItalic}>
                                  Data Coming Soon
                                </Typography>
                              );
                            }
                            return file && hasFileError(file) ? (
                              <Box sx={styles.classifiederror}>
                                <Box
                                  sx={styles.errorIconContainer}
                                >
                                  <SvgIcon fontSize="small" viewBox="0 0 16 16" sx={styles.classifiedsvg}>
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M14.4001 8.0001C14.4001 11.5347 11.5347 14.4001 8.0001 14.4001C4.46548 14.4001 1.6001 11.5347 1.6001 8.0001C1.6001 4.46548 4.46548 1.6001 8.0001 1.6001C11.5347 1.6001 14.4001 4.46548 14.4001 8.0001ZM8.0001 4.0001C8.33147 4.0001 8.6001 4.26873 8.6001 4.6001V8.2001C8.6001 8.53147 8.33147 8.8001 8.0001 8.8001C7.66873 8.8001 7.4001 8.53147 7.4001 8.2001V4.6001C7.4001 4.26873 7.66873 4.0001 8.0001 4.0001ZM8.0001 12.0001C8.44193 12.0001 8.8001 11.6419 8.8001 11.2001C8.8001 10.7583 8.44193 10.4001 8.0001 10.4001C7.55827 10.4001 7.2001 10.7583 7.2001 11.2001C7.2001 11.6419 7.55827 12.0001 8.0001 12.0001Z"
                                      fill="currentColor"
                                    />
                                  </SvgIcon>
                                </Box>
                                <Typography variant="body2" sx={styles.classifiedErrorText}>
                                  We are not able to render this file, please
                                  {' '}
                                  <Box
                                    component="a"
                                    href={config.airworks_SupportUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={styles.generrormessage}
                                  >
                                    Contact us
                                  </Box>
                                  {' '}
                                  for support.
                                </Typography>
                              </Box>
                            ) : null;
                          })()}

                          {/* Render layers with colors */}
                          {selectedGeoDbFile && geodatabaseLayersList.length > 0 && (() => {
                            const file = geoDbFiles.find((f: IGeoDbFile) => f._id === selectedGeoDbFile);
                            return file && !hasFileError(file) ? (
                              <>
                                {renderLayerList(
                                  geodatabaseLayersList,
                                  geodatabaseLayers,
                                  handleGeodatabaseLayerToggle,
                                  geodatabaseShowAll,
                                  5,
                                )}
                                {geodatabaseLayersList.length > 5 && (
                                  <Box sx={styles.maxshowbox}>
                                    <Typography
                                      variant="caption"
                                      sx={styles.viewclass}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setGeodatabaseShowAll(!geodatabaseShowAll);
                                      }}
                                    >
                                      {geodatabaseShowAll ? 'SHOW LESS' : 'VIEW ALL LAYERS'}
                                    </Typography>
                                  </Box>
                                )}
                              </>
                            ) : null;
                          })()}
                        </>
                      )}
                    </Box>
                  </Collapse>

                  <Divider />

                  {/* Khora "Dashboard Layers" now live in the Dashboard panel (Features/dashboard) so
                      all analysis has one home; the Sidebar no longer duplicates them. */}

                  {/* --- CLASSIFIED POINT CLOUD Section --- */}
                  <ListItemButton
                    sx={styles.expansionPanelHeader}
                    onClick={() => setClassifiedPointCloudExpanded(!classifiedPointCloudExpanded)}
                  >
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={allClassesVisible}
                          indeterminate={someClassesVisible && !allClassesVisible}
                          onChange={() => {
                            const makeVisible = !allClassesVisible;
                            recentclassifiedLas.forEach((las) => {
                              setAllClassesVisibility(getLasTitle(las), makeVisible);
                            });
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      label={(
                        <Typography variant="h5" sx={styles.layerName}>
                          Classified Point Cloud
                        </Typography>
                      )}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {/* SLIDER ICON - Toggles standalone panel */}
                    {hasClassifiedPointClouds && (
                      <IconButton
                        ref={adjustButtonRef}
                        sx={styles.sliderbtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAdjustPanelClick('classified', e);
                        }}
                      >
                        <SvgIcon sx={styles.adjustpannelicon} viewBox="0 0 20 20">
                          <rect fill="#ffffff" width="20" height="20" />
                          <g transform="rotate(90 10 10)">
                            <path
                              d="M6.56176 10.625C6.56172 11.096 6.40962 11.5545 6.1281 11.9321C5.84658 12.3098 5.45067 12.5865 4.99926 12.7211V16.875C4.99926 17.0408 4.93341 17.1997 4.8162 17.3169C4.69899 17.4342 4.54002 17.5 4.37426 17.5C4.2085 17.5 4.04953 17.4342 3.93232 17.3169C3.81511 17.1997 3.74926 17.0408 3.74926 16.875V12.7211C3.29799 12.5864 2.90225 12.3096 2.62087 11.932C2.33949 11.5543 2.1875 11.0959 2.1875 10.625C2.1875 10.1541 2.33949 9.69568 2.62087 9.31803C2.90225 8.94038 3.29799 8.66362 3.74926 8.52891V3.125C3.74926 2.95924 3.81511 2.80027 3.93232 2.68306C4.04953 2.56585 4.2085 2.5 4.37426 2.5C4.54002 2.5 4.69899 2.56585 4.8162 2.68306C4.93341 2.80027 4.99926 2.95924 4.99926 3.125V8.52891C5.45067 8.66349 5.84658 8.94019 6.1281 9.31785C6.40962 9.69551 6.56172 10.154 6.56176 10.625ZM10.6243 4.77891V3.125C10.6243 2.95924 10.5584 2.80027 10.4412 2.68306C10.324 2.56585 10.165 2.5 9.99926 2.5C9.8335 2.5 9.67453 2.56585 9.55732 2.68306C9.44011 2.80027 9.37426 2.95924 9.37426 3.125V4.77891C8.92299 4.91362 8.52725 5.19038 8.24587 5.56803C7.96449 5.94568 7.8125 6.40405 7.8125 6.875C7.8125 7.34595 7.96449 7.80432 8.24587 8.18197C8.52725 8.55962 8.92299 8.83638 9.37426 8.97109V16.875C9.37426 17.0408 9.44011 17.1997 9.55732 17.3169C9.67453 17.4342 9.8335 17.5 9.99926 17.5C10.165 17.5 10.324 17.4342 10.4412 17.3169C10.5584 17.1997 10.6243 17.0408 10.6243 16.875V8.97109C11.0755 8.83638 11.4713 8.55962 11.7527 8.18197C12.034 7.80432 12.186 7.34595 12.186 6.875C12.186 6.40405 12.034 5.94568 11.7527 5.56803C11.4713 5.19038 11.0755 4.91362 10.6243 4.77891ZM16.2493 11.0289V3.125C16.2493 2.95924 16.1834 2.80027 16.0662 2.68306C15.949 2.56585 15.79 2.5 15.6243 2.5C15.4585 2.5 15.2995 2.56585 15.1823 2.68306C15.0651 2.80027 14.9993 2.95924 14.9993 3.125V11.0289C14.548 11.1636 14.1522 11.4404 13.8709 11.818C13.5895 12.1957 13.4375 12.6541 13.4375 13.125C13.4375 13.5959 13.5895 14.0543 13.8709 14.432C14.1522 14.8096 14.548 15.0864 14.9993 15.2211V16.875C14.9993 17.0408 15.0651 17.1997 15.1823 17.3169C15.2995 17.4342 15.4585 17.5 15.6243 17.5C15.79 17.5 15.949 17.4342 16.0662 17.3169C16.1834 17.1997 16.2493 17.0408 16.2493 16.875V15.2211C16.7005 15.0864 17.0963 14.8096 17.3777 14.432C17.659 14.0543 17.811 13.5959 17.811 13.125C17.811 12.6541 17.659 12.1957 17.3777 11.818C17.0963 11.4404 16.7005 11.1636 16.2493 11.0289Z"
                              fill="#788898"
                            />
                          </g>
                        </SvgIcon>
                      </IconButton>
                    )}
                    <IconButton sx={styles.button}>
                      {classifiedPointCloudExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                    </IconButton>
                  </ListItemButton>
                  <Collapse in={classifiedPointCloudExpanded}>
                    <Box sx={styles.wrapper}>
                      {recentclassifiedLas.length > 0 ? (
                        recentclassifiedLas.map((las) => {
                          const title = getLasTitle(las);
                          const isVisible = lasFilesDisplayStatuses[title] ?? false;
                          const hasError = hasPointCloudError(las);

                          // Legend rows: classes present in the file when statistics
                          // are available, the full named list otherwise
                          const { classes: legendClasses, isFallback } = getLegendClasses(las.lasType, las.newClasses, classesPresentByFile[title] ?? null);
                          // The Unknown bucket row only exists in fallback mode —
                          // with presence data every class has its own row
                          const classListWithUnknown = isFallback ? [...legendClasses, UNKNOWN_CLASS] : legendClasses;
                          const isShowingAll = !!showAllClassesByFile[las._id];
                          const classesToRender = isShowingAll ? classListWithUnknown : classListWithUnknown.slice(0, MAX_TO_SHOW);

                          return (
                            <Box key={las._id} sx={styles.mastertogglesx}>
                              {/* ──────── Show error message if there's an error ──────── */}
                              {hasError ? (
                                <Box sx={styles.classifiederror}>
                                  <Box
                                    sx={styles.errorIconContainer}
                                  >
                                    <SvgIcon fontSize="small" viewBox="0 0 16 16" sx={styles.classifiedsvg}>
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M14.4001 8.0001C14.4001 11.5347 11.5347 14.4001 8.0001 14.4001C4.46548 14.4001 1.6001 11.5347 1.6001 8.0001C1.6001 4.46548 4.46548 1.6001 8.0001 1.6001C11.5347 1.6001 14.4001 4.46548 14.4001 8.0001ZM8.0001 4.0001C8.33147 4.0001 8.6001 4.26873 8.6001 4.6001V8.2001C8.6001 8.53147 8.33147 8.8001 8.0001 8.8001C7.66873 8.8001 7.4001 8.53147 7.4001 8.2001V4.6001C7.4001 4.26873 7.66873 4.0001 8.0001 4.0001ZM8.0001 12.0001C8.44193 12.0001 8.8001 11.6419 8.8001 11.2001C8.8001 10.7583 8.44193 10.4001 8.0001 10.4001C7.55827 10.4001 7.2001 10.7583 7.2001 11.2001C7.2001 11.6419 7.55827 12.0001 8.0001 12.0001Z"
                                        fill="currentColor"
                                      />
                                    </SvgIcon>
                                  </Box>
                                  {/* if the errorError !== success, error icon will be rendered */}
                                  <Typography variant="body2" sx={styles.classifiedErrorText}>
                                    We are not able to render this file, please
                                    {' '}
                                    <Box
                                      component="a"
                                      href={config.airworks_SupportUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={styles.generrormessage}
                                    >
                                      Contact us
                                    </Box>
                                    {' '}
                                    for support.
                                  </Typography>
                                </Box>
                              ) : (
                                <Box sx={styles.laslegendtitle}>
                                  {classesToRender.map((cls) => {
                                    const classId = String(cls.classId);
                                    const visible = classVisibility[title]?.[classId] !== false;

                                    return (
                                      <Box
                                        key={cls.classId}
                                        sx={styles.lasclassid}
                                      >
                                        {/* Colored circle */}
                                        <Box
                                          sx={{
                                            ...styles.lascolor,
                                            backgroundColor: cls.color,
                                            ...(cls.classId === -1 && styles.unknowoutline),
                                          }}
                                        />
                                        {/* Class name */}
                                        <Typography
                                          variant="body2"
                                          sx={styles.lasnametypo}
                                        >
                                          {cls.name}
                                        </Typography>

                                        {/* Eye-toggle (optional – you can keep it disabled) */}
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (cls.classId === UNKNOWN_CLASS.classId) {
                                              toggleUnknownClassVisibility(title, !visible);
                                            } else {
                                              toggleClassVisibility(title, classId, !visible);
                                            }
                                          }}
                                          sx={styles.eyetogglelegend}
                                        >
                                          <SvgIcon fontSize="small">
                                            {visible ? (
                                              // eye-open
                                              <path d="M10 4.1665C5.90909 4.1665 2.41545 6.71105 1 10.3029C2.41545 13.8947 5.90909 16.4392 10 16.4392C14.0909 16.4392 17.5845 13.8947 19 10.3029C17.5845 6.71105 14.0909 4.1665 10 4.1665ZM10 14.3938C7.74182 14.3938 5.90909 12.561 5.90909 10.3029C5.90909 8.04469 7.74182 6.21196 10 6.21196C12.2582 6.21196 14.0909 8.04469 14.0909 10.3029C14.0909 12.561 12.2582 14.3938 10 14.3938ZM10 7.84832C8.64182 7.84832 7.54545 8.94469 7.54545 10.3029C7.54545 11.661 8.64182 12.7574 10 12.7574C11.3582 12.7574 12.4545 11.661 12.4545 10.3029C12.4545 8.94469 11.3582 7.84832 10 7.84832Z" />
                                            ) : (
                                              // eye-closed
                                              <path d="M19.5872 1.06055L15.0764 5.57031C16.7934 6.66308 18.1433 8.283 18.8997 10.2021C17.4842 13.7939 13.9905 16.3388 9.89966 16.3389C8.27973 16.3389 6.754 15.9384 5.41333 15.2334L1.06079 19.5869L0.212158 18.7383L4.35864 14.5908C2.81491 13.5073 1.60167 11.9836 0.899658 10.2021C2.31516 6.61043 5.80882 4.06641 9.89966 4.06641C11.3582 4.06643 12.7402 4.39068 13.9797 4.96973L18.7385 0.211914L19.5872 1.06055ZM11.885 8.76172C12.1787 9.16626 12.3537 9.66269 12.3538 10.2021C12.3538 11.5603 11.2578 12.6572 9.89966 12.6572C9.36009 12.6572 8.86285 12.4822 8.45825 12.1885L7.29321 13.3535C8.00122 13.9399 8.90925 14.293 9.89966 14.293C12.1578 14.2929 13.9905 12.4603 13.9905 10.2021C13.9904 9.21179 13.6374 8.30367 13.051 7.5957L11.885 8.76172ZM9.89966 6.11133C7.64153 6.11133 5.80892 7.94404 5.80884 10.2021C5.80884 11.0367 6.0599 11.8127 6.4895 12.46L7.68677 11.2627C7.53306 10.9417 7.44458 10.5826 7.44458 10.2021C7.44467 8.84404 8.54153 7.74805 9.89966 7.74805C10.2799 7.74807 10.6383 7.83663 10.9592 7.99023L12.1575 6.79199C11.5102 6.36239 10.7342 6.11136 9.89966 6.11133Z" />
                                            )}
                                          </SvgIcon>
                                        </IconButton>
                                      </Box>
                                    );
                                  })}
                                  {classListWithUnknown.length > MAX_TO_SHOW && (
                                    <Box sx={styles.maxshowbox}>
                                      <Typography
                                        variant="caption"
                                        sx={styles.viewclass}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShowAllClassesByFile((prev) => ({
                                            ...prev,
                                            [las._id]: !prev[las._id],
                                          }));
                                        }}
                                      >
                                        {isShowingAll ? 'SHOW LESS' : 'VIEW ALL CLASSES'}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          );
                        })
                      ) : (

                        <Typography variant="body2" sx={styles.fileItalic}>
                          {placedOrders.length === 0 ? 'No Order Placed' : 'Data Coming Soon'}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                  {/* --- CAD Drawings Section --- */}
                  <Box sx={styles.cadbox}>
                    {selectedOrder ? (
                      <CadDrawings
                        key={selectedOrder._id}
                        order={selectedOrder!}
                        index={1}
                        openAdjustPanel={openAdjustPanel}
                        onAdjustPanelPositionChange={onAdjustPanelPositionChange}
                      />
                    ) : (
                      <>
                        <Divider />
                        <Box sx={styles.layerSection}>
                          <ListItemButton
                            sx={styles.expansionPanelHeader}
                            onClick={() => setNoOrder2DExpanded(!noOrder2DExpanded)}
                          >
                            <FormControlLabel
                              control={<Checkbox disabled />}
                              label={<Typography variant="h5" sx={styles.layerName}>2D</Typography>}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <IconButton sx={styles.button}>
                              {noOrder2DExpanded ? (
                                <KeyboardArrowDownIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowRightIcon fontSize="small" />
                              )}
                            </IconButton>
                          </ListItemButton>
                          <Collapse in={noOrder2DExpanded}>
                            <Box sx={styles.wrapper}>
                              <Typography variant="body2" sx={styles.fileItalic}>
                                No Order Placed
                              </Typography>
                            </Box>
                          </Collapse>
                        </Box>
                        <Divider />
                        <Box sx={styles.layerSection}>
                          <ListItemButton
                            sx={styles.expansionPanelHeader}
                            onClick={() => setNoOrder3DExpanded(!noOrder3DExpanded)}
                          >
                            <FormControlLabel
                              control={<Checkbox disabled />}
                              label={<Typography variant="h5" sx={styles.layerName}>3D</Typography>}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <IconButton sx={styles.button}>
                              {noOrder3DExpanded ? (
                                <KeyboardArrowDownIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowRightIcon fontSize="small" />
                              )}
                            </IconButton>
                          </ListItemButton>
                          <Collapse in={noOrder3DExpanded}>
                            <Box sx={styles.wrapper}>
                              <Typography variant="body2" sx={styles.fileItalic}>
                                No Order Placed
                              </Typography>
                            </Box>
                          </Collapse>
                        </Box>
                        <Divider />
                      </>
                    )}
                  </Box>
                  {/* --- Point Cloud Section --- */}
                  <ListItemButton
                    sx={styles.expansionPanelHeader}
                    onClick={() => setUnClassifiedPointCloudExpanded(!unclassifiedPointCloudExpanded)}
                  >
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={showPointCloud}
                          indeterminate={somePointCloudVisible && !showPointCloud}
                          onChange={(e) => {
                            const newVisibility = !showPointCloud;
                            setShowPointCloud(newVisibility);

                            // Toggle all unclassified point cloud layers
                            unclassifiedLas.forEach((las) => {
                              const title = getLasTitle(las);
                              toggleLasVisibility(title, newVisibility);
                            });
                          }}
                          name="pointCloud"
                          color="primary"
                          sx={styles.checkboxpc}
                        />
                      )}
                      label={(
                        <Typography variant="h5" sx={styles.layerNamePc}>
                          Point Cloud
                        </Typography>
                      )}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {hasUnclassifiedPointClouds && (
                      <IconButton
                        ref={adjustButtonRef}
                        sx={styles.sliderbtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAdjustPanelClick('input', e);
                        }}
                      >
                        <SvgIcon sx={styles.unclassadjustpannelicon} viewBox="0 0 20 20">
                          <rect fill="#ffffff" width="20" height="20" />
                          <g transform="rotate(90 10 10)">
                            <path
                              d="M6.56176 10.625C6.56172 11.096 6.40962 11.5545 6.1281 11.9321C5.84658 12.3098 5.45067 12.5865 4.99926 12.7211V16.875C4.99926 17.0408 4.93341 17.1997 4.8162 17.3169C4.69899 17.4342 4.54002 17.5 4.37426 17.5C4.2085 17.5 4.04953 17.4342 3.93232 17.3169C3.81511 17.1997 3.74926 17.0408 3.74926 16.875V12.7211C3.29799 12.5864 2.90225 12.3096 2.62087 11.932C2.33949 11.5543 2.1875 11.0959 2.1875 10.625C2.1875 10.1541 2.33949 9.69568 2.62087 9.31803C2.90225 8.94038 3.29799 8.66362 3.74926 8.52891V3.125C3.74926 2.95924 3.81511 2.80027 3.93232 2.68306C4.04953 2.56585 4.2085 2.5 4.37426 2.5C4.54002 2.5 4.69899 2.56585 4.8162 2.68306C4.93341 2.80027 4.99926 2.95924 4.99926 3.125V8.52891C5.45067 8.66349 5.84658 8.94019 6.1281 9.31785C6.40962 9.69551 6.56172 10.154 6.56176 10.625ZM10.6243 4.77891V3.125C10.6243 2.95924 10.5584 2.80027 10.4412 2.68306C10.324 2.56585 10.165 2.5 9.99926 2.5C9.8335 2.5 9.67453 2.56585 9.55732 2.68306C9.44011 2.80027 9.37426 2.95924 9.37426 3.125V4.77891C8.92299 4.91362 8.52725 5.19038 8.24587 5.56803C7.96449 5.94568 7.8125 6.40405 7.8125 6.875C7.8125 7.34595 7.96449 7.80432 8.24587 8.18197C8.52725 8.55962 8.92299 8.83638 9.37426 8.97109V16.875C9.37426 17.0408 9.44011 17.1997 9.55732 17.3169C9.67453 17.4342 9.8335 17.5 9.99926 17.5C10.165 17.5 10.324 17.4342 10.4412 17.3169C10.5584 17.1997 10.6243 17.0408 10.6243 16.875V8.97109C11.0755 8.83638 11.4713 8.55962 11.7527 8.18197C12.034 7.80432 12.186 7.34595 12.186 6.875C12.186 6.40405 12.034 5.94568 11.7527 5.56803C11.4713 5.19038 11.0755 4.91362 10.6243 4.77891ZM16.2493 11.0289V3.125C16.2493 2.95924 16.1834 2.80027 16.0662 2.68306C15.949 2.56585 15.79 2.5 15.6243 2.5C15.4585 2.5 15.2995 2.56585 15.1823 2.68306C15.0651 2.80027 14.9993 2.95924 14.9993 3.125V11.0289C14.548 11.1636 14.1522 11.4404 13.8709 11.818C13.5895 12.1957 13.4375 12.6541 13.4375 13.125C13.4375 13.5959 13.5895 14.0543 13.8709 14.432C14.1522 14.8096 14.548 15.0864 14.9993 15.2211V16.875C14.9993 17.0408 15.0651 17.1997 15.1823 17.3169C15.2995 17.4342 15.4585 17.5 15.6243 17.5C15.79 17.5 15.949 17.4342 16.0662 17.3169C16.1834 17.1997 16.2493 17.0408 16.2493 16.875V15.2211C16.7005 15.0864 17.0963 14.8096 17.3777 14.432C17.659 14.0543 17.811 13.5959 17.811 13.125C17.811 12.6541 17.659 12.1957 17.3777 11.818C17.0963 11.4404 16.7005 11.1636 16.2493 11.0289Z"
                              fill="#788898"
                            />
                          </g>
                        </SvgIcon>
                      </IconButton>
                    )}
                    <IconButton sx={styles.button}>
                      {unclassifiedPointCloudExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                    </IconButton>
                  </ListItemButton>

                  {/* --- LAS File List --- */}
                  <Collapse in={unclassifiedPointCloudExpanded}>
                    <Box sx={styles.wrapper}>
                      <Box sx={styles.lasfileContainer}>
                        {unclassifiedLas.length > 0 ? (
                          unclassifiedLas.map((las) => {
                            const title = getLasTitle(las);
                            const isVisible = lasFilesDisplayStatuses[title] ?? true;
                            const hasError = hasPointCloudError(las);
                            return (
                              <ListItem sx={styles.lasFileListItem} key={las._id}>
                                <Box sx={styles.unclassifiedbox}>
                                  <Box sx={styles.errorIconSpace}>
                                    {hasError && (
                                      <>
                                        <Box
                                          sx={styles.errorIconContainer}
                                          onClick={(e) => handleErrorIconClick(e, las._id)}
                                        >
                                          <SvgIcon fontSize="small" viewBox="0 0 16 16">
                                            <path
                                              fillRule="evenodd"
                                              clipRule="evenodd"
                                              d="M14.4001 8.0001C14.4001 11.5347 11.5347 14.4001 8.0001 14.4001C4.46548 14.4001 1.6001 11.5347 1.6001 8.0001C1.6001 4.46548 4.46548 1.6001 8.0001 1.6001C11.5347 1.6001 14.4001 4.46548 14.4001 8.0001ZM8.0001 4.0001C8.33147 4.0001 8.6001 4.26873 8.6001 4.6001V8.2001C8.6001 8.53147 8.33147 8.8001 8.0001 8.8001C7.66873 8.8001 7.4001 8.53147 7.4001 8.2001V4.6001C7.4001 4.26873 7.66873 4.0001 8.0001 4.0001ZM8.0001 12.0001C8.44193 12.0001 8.8001 11.6419 8.8001 11.2001C8.8001 10.7583 8.44193 10.4001 8.0001 10.4001C7.55827 10.4001 7.2001 10.7583 7.2001 11.2001C7.2001 11.6419 7.55827 12.0001 8.0001 12.0001Z"
                                              fill="#FF0000"
                                            />
                                          </SvgIcon>
                                        </Box>
                                        <Popover
                                          open={errorTooltipFileId === las._id}
                                          anchorEl={errorTooltipAnchor}
                                          onClose={handleErrorTooltipClose}
                                          anchorOrigin={{
                                            vertical: 'top',
                                            horizontal: 'center',
                                          }}
                                          transformOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'center',
                                          }}
                                          sx={styles.popover}
                                        >
                                          <Typography variant="body2" sx={styles.errorTooltipText}>
                                            There's an issue with your .las file. Please contact support for further information.
                                            {' '}
                                            <Box
                                              component="a"
                                              href={config.airworks_SupportUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              sx={styles.generrormessage}
                                            >
                                              Contact us
                                            </Box>
                                          </Typography>
                                        </Popover>
                                      </>
                                    )}
                                  </Box>
                                  {/* File Name */}
                                  <Typography
                                    variant="body2"
                                    sx={styles.lasfile}
                                  >
                                    {title}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleLasVisibility(title, !isVisible);
                                      setTimeout(() => {
                                        const anyVisible = unclassifiedLas.some((las) => {
                                          const t = getLasTitle(las);
                                          return lasFilesDisplayStatuses[t] !== false;
                                        });
                                        setShowPointCloud(anyVisible);
                                      }, 50);
                                    }}
                                    sx={styles.eyetoggleopac}
                                  >
                                    <SvgIcon fontSize="small">
                                      {isVisible ? (
                                        // eye-open
                                        <path d="M10 4.1665C5.90909 4.1665 2.41545 6.71105 1 10.3029C2.41545 13.8947 5.90909 16.4392 10 16.4392C14.0909 16.4392 17.5845 13.8947 19 10.3029C17.5845 6.71105 14.0909 4.1665 10 4.1665ZM10 14.3938C7.74182 14.3938 5.90909 12.561 5.90909 10.3029C5.90909 8.04469 7.74182 6.21196 10 6.21196C12.2582 6.21196 14.0909 8.04469 14.0909 10.3029C14.0909 12.561 12.2582 14.3938 10 14.3938ZM10 7.84832C8.64182 7.84832 7.54545 8.94469 7.54545 10.3029C7.54545 11.661 8.64182 12.7574 10 12.7574C11.3582 12.7574 12.4545 11.661 12.4545 10.3029C12.4545 8.94469 11.3582 7.84832 10 7.84832Z" />
                                      ) : (
                                        // eye-closed
                                        <path d="M19.5872 1.06055L15.0764 5.57031C16.7934 6.66308 18.1433 8.283 18.8997 10.2021C17.4842 13.7939 13.9905 16.3388 9.89966 16.3389C8.27973 16.3389 6.754 15.9384 5.41333 15.2334L1.06079 19.5869L0.212158 18.7383L4.35864 14.5908C2.81491 13.5073 1.60167 11.9836 0.899658 10.2021C2.31516 6.61043 5.80882 4.06641 9.89966 4.06641C11.3582 4.06643 12.7402 4.39068 13.9797 4.96973L18.7385 0.211914L19.5872 1.06055ZM11.885 8.76172C12.1787 9.16626 12.3537 9.66269 12.3538 10.2021C12.3538 11.5603 11.2578 12.6572 9.89966 12.6572C9.36009 12.6572 8.86285 12.4822 8.45825 12.1885L7.29321 13.3535C8.00122 13.9399 8.90925 14.293 9.89966 14.293C12.1578 14.2929 13.9905 12.4603 13.9905 10.2021C13.9904 9.21179 13.6374 8.30367 13.051 7.5957L11.885 8.76172ZM9.89966 6.11133C7.64153 6.11133 5.80892 7.94404 5.80884 10.2021C5.80884 11.0367 6.0599 11.8127 6.4895 12.46L7.68677 11.2627C7.53306 10.9417 7.44458 10.5826 7.44458 10.2021C7.44467 8.84404 8.54153 7.74805 9.89966 7.74805C10.2799 7.74807 10.6383 7.83663 10.9592 7.99023L12.1575 6.79199C11.5102 6.36239 10.7342 6.11136 9.89966 6.11133Z" />
                                      )}
                                    </SvgIcon>
                                  </IconButton>
                                </Box>
                              </ListItem>
                            );
                          })
                        ) : (
                          <Typography variant="body2" sx={styles.fileItalic}>
                            No Data Available
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Collapse>
                  <Divider />

                  {/* ==================== SHAPEFILE SECTION ==================== */}
                  <ListItemButton
                    sx={styles.expansionPanelHeader}
                    onClick={() => setShapefileExpanded(!shapefileExpanded)}
                  >
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={shpFiles.length === 0 ? false : shapefileChecked}
                          disabled={shpFiles.length === 0}
                          indeterminate={
                            shpFiles.length > 0 &&
                            !shapefileChecked &&
                            Object.values(shapefileLayers).some((v) => v)
                          }
                          onChange={handleShapefileMasterToggle}
                          onClick={(e) => e.stopPropagation()}
                          color="primary"
                        />
                      )}
                      label={(
                        <Typography variant="h5" sx={styles.layerName}>
                          Shapefile
                        </Typography>
                      )}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <IconButton sx={styles.button}>
                      {shapefileExpanded ? (
                        <KeyboardArrowDownIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowRightIcon fontSize="small" />
                      )}
                    </IconButton>
                  </ListItemButton>

                  <Collapse in={shapefileExpanded}>
                    <Box sx={styles.wrapper}>
                      {shpFiles.length === 0 ? (
                        <Typography variant="body2" sx={styles.fileItalic}>
                          {placedOrders.length === 0 ? 'No Order Placed' : 'Data Coming Soon'}
                        </Typography>
                      ) : (
                        <>
                          <Select
                            value={selectedShpFile || ''}
                            onChange={handleShapefileFileChange}
                            displayEmpty
                            variant="standard"
                            disableUnderline
                            sx={styles.ongroundfilechange}
                            IconComponent={KeyboardArrowDownIcon}
                          >
                            {shpFiles.map((file: IShpFile) => (
                              <MenuItem key={file._id} value={file._id}>
                                {getFileName(file.filename)}
                              </MenuItem>
                            ))}
                          </Select>

                          {/* Error Message */}
                          {selectedShpFile && (() => {
                            const file = shpFiles.find((f: IShpFile) => f._id === selectedShpFile);
                            if (file && !file.esriUrl) {
                              return (
                                <Typography variant="body2" sx={styles.fileItalic}>
                                  Data Coming Soon
                                </Typography>
                              );
                            }
                            return file && hasFileError(file) ? (
                              <Box sx={styles.classifiederror}>
                                <Box
                                  sx={styles.errorIconContainer}
                                >
                                  <SvgIcon fontSize="small" viewBox="0 0 16 16" sx={styles.classifiedsvg}>
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M14.4001 8.0001C14.4001 11.5347 11.5347 14.4001 8.0001 14.4001C4.46548 14.4001 1.6001 11.5347 1.6001 8.0001C1.6001 4.46548 4.46548 1.6001 8.0001 1.6001C11.5347 1.6001 14.4001 4.46548 14.4001 8.0001ZM8.0001 4.0001C8.33147 4.0001 8.6001 4.26873 8.6001 4.6001V8.2001C8.6001 8.53147 8.33147 8.8001 8.0001 8.8001C7.66873 8.8001 7.4001 8.53147 7.4001 8.2001V4.6001C7.4001 4.26873 7.66873 4.0001 8.0001 4.0001ZM8.0001 12.0001C8.44193 12.0001 8.8001 11.6419 8.8001 11.2001C8.8001 10.7583 8.44193 10.4001 8.0001 10.4001C7.55827 10.4001 7.2001 10.7583 7.2001 11.2001C7.2001 11.6419 7.55827 12.0001 8.0001 12.0001Z"
                                      fill="currentColor"
                                    />
                                  </SvgIcon>
                                </Box>
                                <Typography variant="body2" sx={styles.classifiedErrorText}>
                                  We are not able to render this file, please
                                  {' '}
                                  <Box
                                    component="a"
                                    href={config.airworks_SupportUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={styles.generrormessage}
                                  >
                                    Contact us
                                  </Box>
                                  {' '}
                                  for support.
                                </Typography>
                              </Box>
                            ) : null;
                          })()}

                          {/* Render layers with colors */}
                          {selectedShpFile && shapefileLayersList.length > 0 && (() => {
                            const file = shpFiles.find((f: IShpFile) => f._id === selectedShpFile);
                            return file && !hasFileError(file) ? (
                              <>
                                {renderLayerList(
                                  shapefileLayersList,
                                  shapefileLayers,
                                  handleShapefileLayerToggle,
                                  shapefileShowAll,
                                  5,
                                )}
                                {shapefileLayersList.length > 5 && (
                                  <Box sx={styles.maxshowbox}>
                                    <Typography
                                      variant="caption"
                                      sx={styles.viewclass}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShapefileShowAll(!shapefileShowAll);
                                      }}
                                    >
                                      {shapefileShowAll ? 'SHOW LESS' : 'VIEW ALL LAYERS'}
                                    </Typography>
                                  </Box>
                                )}
                              </>
                            ) : null;
                          })()}
                        </>
                      )}
                    </Box>
                  </Collapse>
                  <Divider />

                  {/* ==================== KML BOUNDARY SECTION (AUT-6431) ==================== */}
                  <ListItemButton
                    sx={styles.expansionPanelHeader}
                    disabled={!kmlBoundaryAvailable}
                    onClick={onKmlBoundaryToggle}
                  >
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={kmlBoundaryAvailable && kmlBoundaryVisible}
                          disabled={!kmlBoundaryAvailable}
                          onChange={onKmlBoundaryToggle}
                          onClick={(e) => e.stopPropagation()}
                          color="primary"
                        />
                      )}
                      label={(
                        <Typography variant="h5" sx={styles.layerName}>
                          KML Boundary
                        </Typography>
                      )}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </ListItemButton>

                  <Divider />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Sidebar>
    </Box>
  );
};

export default FilesSidebar;
